import { ReactNode } from "react";
import { Card, Flex, Form, Switch, Typography } from "antd";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";
import { sectionLabelStyle } from "components/NavSectionLabel.component";

const { Text } = Typography;

// The settings page vocabulary shared by Notifikasi, Pembatasan Admin and the
// two reward programs: a page frame, one section per group (its name in a
// left column, a bordered card of rows on the right), and the row shapes
// inside a card. ToggleRow and optionBlockStyle are also used by the staff
// role cards on the instructor detail page.

// One group of settings: its name (and an optional one-line description) in
// a left column, the bordered card holding the group's rows on the right.
// `span` puts the name above and lets the card take the page's full width,
// for content that is not settings rows (Referral Menunggu's table). Below a
// 1100px window every section stacks; from 1760px the sections flow into two
// columns (index.css).
export const SettingsSection = ({
  id,
  title,
  description,
  children,
  span,
  flush,
}: {
  /** Anchor, so a link can open the page at this section. */
  id?: string;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  span?: boolean;
  /** Card body without side padding (a table brings its own). */
  flush?: boolean;
}) => {
  const heading = (
    <>
      <Text role="heading" aria-level={2} style={{ ...sectionLabelStyle, display: "block" }}>
        {title}
      </Text>
      {description && (
        <Text
          type="secondary"
          className="settings-desc"
          style={{ display: "block", fontSize: textPx(13), lineHeight: 1.5, marginTop: 4 }}
        >
          {description}
        </Text>
      )}
    </>
  );
  const card = (
    <Card
      style={{ borderRadius: 16, borderColor: colors.line }}
      styles={{ body: { padding: flush ? 0 : "4px 24px" } }}
    >
      {children}
    </Card>
  );
  if (span) {
    return (
      <div id={id} className="settings-section settings-section--span settings-section--full">
        <div style={{ marginBottom: 10 }}>{heading}</div>
        {card}
      </div>
    );
  }
  return (
    <div id={id} className="settings-section">
      <div className="settings-section__side">{heading}</div>
      {card}
    </div>
  );
};

// A row aligned with the cards' column (the save footer under the sections).
// Spans both columns on a wide screen, so Save stays under everything.
export const SettingsSectionsFooter = ({ children }: { children: ReactNode }) => (
  <div className="settings-section settings-section--full settings-sections__footer">
    <div />
    <Flex justify="flex-end" align="center" gap={10}>
      {children}
    </Flex>
  </div>
);

// Caps the whole page (heading included) at the sections' width, so the
// heading's actions line up with the cards' right edge. On a wide screen the
// two-column page is centered (index.css).
export const SettingsPage = ({ children }: { children: ReactNode }) => (
  <div className="settings-page">{children}</div>
);

// The column of sections a settings page is built from.
export const SettingsSections = ({ children }: { children: ReactNode }) => (
  <div className="settings-sections">{children}</div>
);

// The switch itself is the form field (noStyle keeps the row compact). The
// text stops at 560px (about 85 characters of description) so the label never
// sits far from its switch.
export const ToggleRow = ({
  name,
  labelKey,
  descKey,
}: {
  name: string;
  labelKey: string;
  descKey?: string;
}) => (
  <Flex justify="space-between" align="center" gap={24}>
    <div style={{ minWidth: 0, maxWidth: 560 }}>
      <div style={{ fontWeight: 600, fontSize: textPx(14.5) }}>{translate(labelKey)}</div>
      {descKey && (
        <Text
          type="secondary"
          className="settings-desc"
          style={{ fontSize: textPx(13), lineHeight: 1.5, display: "block", marginTop: 2 }}
        >
          {translate(descKey)}
        </Text>
      )}
    </div>
    <Form.Item name={name} valuePropName="checked" noStyle>
      <Switch aria-label={translate(labelKey)} style={{ flexShrink: 0 }} />
    </Form.Item>
  </Flex>
);

// One row of a settings card: label and hint on the left, the control on the
// right, a hairline below. `below` renders full-width under the row, for an
// input that appears when its switch is on.
export const SettingRow = ({
  title,
  hint,
  control,
  below,
  last,
}: {
  title: string;
  hint?: string;
  control: ReactNode;
  below?: ReactNode;
  last?: boolean;
}) => (
  <div
    style={{
      padding: "16px 0",
      borderBottom: last ? "none" : `1px solid ${colors.line}`,
    }}
  >
    <Flex justify="space-between" align="center" gap={24} wrap="wrap">
      <div style={{ minWidth: 0, maxWidth: 520, flex: "1 1 280px" }}>
        <div style={{ fontWeight: 600, fontSize: textPx(14.5) }}>{title}</div>
        {hint && (
          <Text
            type="secondary"
            className="settings-desc"
            style={{ fontSize: textPx(13), lineHeight: 1.5, display: "block", marginTop: 2 }}
          >
            {hint}
          </Text>
        )}
      </div>
      {/* The control sits against the card's right edge and a validation
          message prints on one line under it: from the box's left edge when
          it fits, else ending at the box's right edge and running out to the
          left (index.css .settings-row-control). So a box never slides left
          when a long message appears. A row holding two controls (a number
          plus a unit select) keeps them in a Flex with justify="flex-end"
          for the same reason. */}
      <div className="settings-row-control">{control}</div>
    </Flex>
    {below}
  </div>
);

// A label that reads into its input, on one line under a row's description,
// with an optional tail after the input so the line reads as one sentence
// ("Ingatkan [7 hari] sebelum kedaluwarsa"). The input's Form.Item goes in
// as children.
export const InlineField = ({
  label,
  suffix,
  children,
}: {
  label: string;
  suffix?: string;
  children: ReactNode;
}) => (
  <Flex align="center" gap={10} wrap="wrap" style={{ marginTop: 12 }}>
    <Text style={{ fontSize: textPx(13) }}>{label}</Text>
    {children}
    {suffix ? <Text style={{ fontSize: textPx(13) }}>{suffix}</Text> : null}
  </Flex>
);

// Hairline between whole options inside a group; the last row carries none.
export const optionBlockStyle = (isLast: boolean) => ({
  padding: "16px 0",
  borderBottom: isLast ? undefined : `1px solid ${colors.line}`,
});
